import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, FileClock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtDateTime } from "@/lib/constants";
import { BrandTag, EmptyState, LoadingBlock, PageHeader } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const Detail = ({ id, onClose }) => {
  const { meta } = useApp();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!id) { setD(null); return; }
    api.get(`/uploads/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="upload-detail-dialog">
        <DialogHeader><DialogTitle>{d?.file_name || "Upload details"}</DialogTitle></DialogHeader>
        {!d ? <LoadingBlock /> : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["New", d.counts.new], ["Updated", d.counts.updated], ["Skipped", d.counts.skipped], ["Problems", d.counts.errors]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-bold">{v ?? 0}</div></div>
              ))}
            </div>
            <div>
              <h4 className="mb-2 font-semibold">Change log</h4>
              {d.changes.length ? (
                <table className="w-full" data-testid="upload-change-log">
                  <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-2">Record</th><th>Field</th><th>Old value</th><th>New value</th></tr></thead>
                  <tbody>{d.changes.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100"><td className="py-2 pr-2">{c.record_label}</td><td className="pr-2">{meta?.labels[c.field] || c.field}</td><td className="pr-2 text-slate-400">{c.old || "empty"}</td><td className="font-medium">{c.new}</td></tr>
                  ))}</tbody>
                </table>
              ) : <p className="text-slate-400">No existing records were changed by this file.</p>}
            </div>
            {d.error_rows.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold">Rows with problems</h4>
                <ul className="space-y-1">{d.error_rows.map((e) => <li key={e.row} className="rounded-lg bg-red-50 px-3 py-2 text-red-800">Row {e.row}: {e.reason}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function History() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/uploads").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  return (
    <div data-testid="history-page">
      <PageHeader title="Upload history" subtitle="Every file that has been imported, and what it changed." />
      {!rows ? <LoadingBlock rows={6} /> : !rows.length ? (
        <EmptyState icon={FileClock} title="No uploads yet" text="When you import a file it will show up here." action={<Button onClick={() => nav("/upload")} data-testid="history-upload-cta">Upload a file</Button>} testid="history-empty" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="history-table">
            <thead className="bg-slate-50"><tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">File</th><th className="px-3">Uploaded by</th><th className="px-3">When</th><th className="px-3">Source</th><th className="px-3">Brand</th><th className="px-3">Period</th><th className="px-3 text-right">New</th><th className="px-3 text-right">Updated</th><th className="px-3 text-right">Skipped</th><th className="px-3" />
            </tr></thead>
            <tbody>{rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`history-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{u.file_name}</td>
                <td className="px-3">{u.user_name}</td>
                <td className="whitespace-nowrap px-3 text-slate-600">{fmtDateTime(u.imported_at || u.created_at)}</td>
                <td className="whitespace-nowrap px-3">{u.source_type} · {u.platform}</td>
                <td className="px-3"><div className="flex flex-wrap gap-1">{(u.brands || []).map((b) => <BrandTag key={b} brand={b} />)}</div></td>
                <td className="px-3">{u.period}</td>
                <td className="px-3 text-right font-semibold text-emerald-700">{u.counts.new}</td>
                <td className="px-3 text-right font-semibold text-indigo-700">{u.counts.updated}</td>
                <td className="px-3 text-right text-slate-500">{u.counts.skipped}</td>
                <td className="px-3 text-right"><Button size="sm" variant="ghost" onClick={() => setOpen(u.id)} data-testid={`history-view-${u.id}`}><Eye className="mr-1 h-4 w-4" />Details</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <Detail id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
